// YNX — useAuth hook
// ─────────────────────────────────────────────────────────────────────
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH
// ─────────────────────────────────────────────────────────────────────
// Encapsulates Emergent-managed Google OAuth:
//   1. Detects `#session_id=...` in URL during render (anti race-condition)
//   2. If present, exchanges it for a 7-day session_token, strips the hash
//   3. Otherwise validates any existing localStorage token via /auth/me
//
// Returned shape:
//   { currentUser, setCurrentUser, authCallbackBusy }
//
// `authCallbackBusy` is `true` only when the OAuth callback is in flight,
// so the routing layer can render a "Connexion Google en cours…" splash
// instead of flashing the landing page underneath.
import { useEffect, useState } from 'react';
import { exchangeSession, getMe, getAuthToken } from '../lib/auth';

// Captured the INSTANT the HTML <head> parses, BEFORE PostHog, GA, SW
// registration or anything else can read/mutate window.location.hash.
// See /app/frontend/public/index.html — the inline <script> at the very
// top of <head> stores #session_id=… into window.__YNX_AUTH_SESSION_ID__.
// Falls back to a live read of location.hash for safety (dev / hot-reload).
const readSessionIdAtBoot = () => {
  try {
    if (typeof window !== 'undefined' && window.__YNX_AUTH_SESSION_ID__) {
      return window.__YNX_AUTH_SESSION_ID__;
    }
    const h = (typeof window !== 'undefined' && window.location.hash) || '';
    const m = h.match(/session_id=([^&]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  } catch (_e) {
    return '';
  }
};

const SESSION_ID_AT_BOOT = readSessionIdAtBoot();

export const useAuth = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [authCallbackBusy, setAuthCallbackBusy] = useState(!!SESSION_ID_AT_BOOT);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (SESSION_ID_AT_BOOT) {
        try {
          const u = await exchangeSession(SESSION_ID_AT_BOOT);
          if (!cancelled) {
            setCurrentUser({
              user_id: u.user_id,
              email: u.email,
              name: u.name,
              picture: u.picture,
            });
          }
        } catch (e) {
          console.warn('YNX auth: exchange failed', e);
        } finally {
          // Strip the hash so refreshes don't re-trigger the exchange
          try {
            const clean = window.location.pathname + window.location.search;
            window.history.replaceState({}, '', clean);
          } catch (e) {
            console.debug('YNX auth: replaceState failed', e);
          }
          if (!cancelled) setAuthCallbackBusy(false);
        }
        return;
      }
      // No callback in URL — validate any existing token silently
      if (getAuthToken()) {
        try {
          const u = await getMe();
          if (!cancelled) setCurrentUser(u);
        } catch (e) {
          console.debug('YNX auth: /me failed, clearing token', e);
          try {
            localStorage.removeItem('ynx_auth_token');
          } catch (e2) {
            console.debug('YNX auth: localStorage clear failed', e2);
          }
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return { currentUser, setCurrentUser, authCallbackBusy };
};
